import React, { useEffect, useState, useCallback } from 'react';
import { rbacAPI } from '../api';
import { toast } from 'sonner';
import { Shield, Save, ArrowLeft, Lock, Plus, Copy, Archive } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LANDING_PAGE_LABELS } from '../lib/roleAccess';

const LANDING_OPTIONS = Object.entries(LANDING_PAGE_LABELS);

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  manager: 'Sales Manager',
  sales_executive: 'Sales Executive',
  architect_manager: 'Architect Manager',
  architect: 'Architect',
  site_engineer: 'Site Engineer',
  accountant: 'Accountant',
  finance_manager: 'Finance Manager'
};

const PERM_LABELS = {
  'dashboard.view': 'View Dashboard',
  'leads.view': 'View Leads',
  'leads.create': 'Add Lead',
  'leads.edit': 'Edit Lead',
  'leads.delete': 'Delete Lead',
  'leads.assign': 'Assign Lead',
  'leads.change_status': 'Change Lead Status',
  'leads.contact.view': 'View Contact Info',
  'leads.contact.edit': 'Edit Contact Info',
  'leads.source.edit': 'Edit Lead Source',
  'leads.sector.manage': 'Manage Client Sector Master',
  'import.sheets.manage': 'Manage Google Sheet Sources',
  'import.history.view_all': 'View All Import History',
  'calendar.view': 'View Calendar',
  'communications.view': 'View Communications',
  'communications.send': 'Send Communications',
  'reports.view': 'View Reports',
  'reports.export': 'Export Reports',
  'settings.view': 'View Settings',
  'settings.manage': 'Manage Settings',
  'users.view': 'View Users',
  'users.create': 'Create User',
  'users.edit': 'Edit User',
  'users.delete': 'Delete User',
  'users.reset_password': 'Reset Password',
  'services.view': 'View Services',
  'services.manage': 'Manage Services'
};

export const RolePermissionsPage = () => {
  const [roles, setRoles] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePerms, setRolePerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState(''); const [description, setDescription] = useState(''); const [landingPage, setLandingPage] = useState('/dashboard');

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        rbacAPI.getRoles(),
        rbacAPI.getAllPermissions()
      ]);
      setRoles(rolesRes.data);
      setGrouped(permsRes.data.grouped);
      const nonSuper = rolesRes.data.find(r => r.role !== 'super_admin');
      if (nonSuper) {
        setSelectedRole(nonSuper.role);
        setRolePerms(nonSuper.permissions);
      }
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectRole = (role) => {
    const found = roles.find(r => r.role === role);
    if (found) {
      setSelectedRole(role);
      setRolePerms(found.permissions);
    }
  };

  const togglePermission = (perm) => {
    setRolePerms(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const toggleGroup = (groupPerms, allChecked) => {
    setRolePerms(prev => {
      if (allChecked) {
        return prev.filter(p => !groupPerms.includes(p));
      } else {
        const set = new Set(prev);
        groupPerms.forEach(p => set.add(p));
        return Array.from(set);
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRole || selectedRole === 'super_admin') return;
    setSaving(true);
    try {
      await rbacAPI.updateRolePermissions(selectedRole, rolePerms);
      toast.success('Permissions updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };
  const createRole = async (copy = false) => { if (!newRole.trim()) return toast.error('Enter a role key'); try { await rbacAPI.createRole({role:newRole,label:newRole.replaceAll('_',' '),description,color:'#3B82F6',icon:'Shield',landing_page:landingPage,sidebar_modules:[],permissions:copy ? rolePerms : []}); setNewRole(''); setDescription(''); fetchData(); toast.success('Custom role created'); } catch(e) { toast.error(e.response?.data?.detail || 'Could not create role'); } };
  const archive = async () => { if (!selectedRole || roles.find(r=>r.role===selectedRole)?.is_system) return; if (!window.confirm('Archive this role?')) return; await rbacAPI.archiveRole(selectedRole); fetchData(); };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  const isSuperAdmin = selectedRole === 'super_admin';

  return (
    <div className="space-y-6" data-testid="roles-container">
      <div className="flex items-center gap-4">
        <Link to="/users" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-4xl font-outfit font-semibold">Role Permissions</h1>
          <p className="text-muted-foreground mt-2">Configure permissions for each role</p>
        </div>
        <div className="ml-auto flex gap-2"><input value={newRole} onChange={e=>setNewRole(e.target.value)} placeholder="custom_role" className="rounded border px-3 py-2 text-sm"/><select value={landingPage} onChange={e=>setLandingPage(e.target.value)} className="rounded border px-2 text-sm" data-testid="new-role-landing-select">{LANDING_OPTIONS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select><button onClick={()=>createRole(false)} className="inline-flex items-center gap-1 rounded bg-primary px-3 py-2 text-sm text-white"><Plus className="h-4 w-4"/>Create role</button></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="clay-card p-4 h-fit">
          <h3 className="font-outfit font-medium mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Roles
          </h3>
          <div className="space-y-1">
            {roles.map(r => (
              <button
                key={r.role}
                data-testid={`role-tab-${r.role}`}
                onClick={() => selectRole(r.role)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedRole === r.role
                    ? 'bg-primary text-white'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <span data-testid={`role-label-${r.role}`}>{r.label || ROLE_LABELS[r.role] || r.role}</span>
                {(r.is_locked || r.is_system) && <Lock className="h-3 w-3 opacity-60" title="Locked system role" />}
              </button>
            ))}
          </div>
          {!isSuperAdmin && <div className="mt-4 border-t pt-3"><label className="text-xs text-muted-foreground">Default landing page</label><select data-testid="role-landing-select" value={roles.find(r=>r.role===selectedRole)?.landing_page || '/dashboard'} onChange={async e=>{await rbacAPI.updateRole(selectedRole,{landing_page:e.target.value});fetchData();}} className="mt-1 w-full rounded border px-2 py-1 text-sm">{LANDING_OPTIONS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select><p className="mt-1 text-[11px] text-muted-foreground">Users only land here if they are authorized; otherwise their first authorized page is used.</p><div className="mt-2 flex gap-2">{!(roles.find(r=>r.role===selectedRole)?.is_locked || roles.find(r=>r.role===selectedRole)?.is_system) && <button onClick={archive} className="text-xs text-red-600" data-testid="role-archive-btn"><Archive className="mr-1 inline h-3 w-3"/>Archive</button>}<button onClick={()=>createRole(true)} className="text-xs text-primary"><Copy className="mr-1 inline h-3 w-3"/>Duplicate selected</button></div></div>}
        </div>

        <div className="lg:col-span-3 clay-card p-6">
          {isSuperAdmin && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4 flex items-start gap-3">
              <Lock className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Super Admin has all permissions</p>
                <p className="text-sm text-muted-foreground mt-1">
                  System-level role with full access. Permissions cannot be modified.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {Object.entries(grouped).map(([group, perms]) => {
              const allChecked = perms.every(p => rolePerms.includes(p));
              const someChecked = perms.some(p => rolePerms.includes(p));

              return (
                <div key={group} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">{group}</h4>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`group-toggle-${group.toLowerCase()}`}
                        checked={allChecked}
                        ref={el => el && (el.indeterminate = !allChecked && someChecked)}
                        disabled={isSuperAdmin}
                        onChange={() => toggleGroup(perms, allChecked)}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-muted-foreground">Select all</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {perms.map(perm => (
                      <label
                        key={perm}
                        className={`flex items-center gap-2 p-2 rounded ${
                          isSuperAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <input
                          type="checkbox"
                          data-testid={`perm-${perm}`}
                          checked={isSuperAdmin ? true : rolePerms.includes(perm)}
                          disabled={isSuperAdmin}
                          onChange={() => togglePermission(perm)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm">{PERM_LABELS[perm] || perm}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {!isSuperAdmin && (
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                data-testid="save-perms-button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
              <span className="text-sm text-muted-foreground">
                {rolePerms.length} permission{rolePerms.length !== 1 ? 's' : ''} enabled
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
